import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import { SelectFileIcon } from '../SVGIcons';
import watermarkPreviewImage from '../../../assets/images/watermark_preview.png';
import { WATERMARK_TYPE, WatermarkType } from '../../reducers/serviceSettings';
import { serviceSettingsActions } from '../../reducers/serviceSettings';
import { UserCircleIcon, UserSettingsIcon } from '../SVGIcons';
import { RoomType, LifestyleType } from '../../constants';

// 型定義
interface MenuItem {
  key: 'profile' | 'service';
  label: string;
  icon: string;
}

interface ProfileData {
  label: string;
  value: string | JSX.Element;
}

// Redux状態管理：ユーザー情報とアカウント詳細を取得
function mapStateToProps(state: any) {
  return {
    currentUser: state.currentUser,
    serviceSettings: state.serviceSettings
  };
}

// ウォーターマークラベルの定義
class WatermarkLabel {
  private static readonly labels = {
    [WATERMARK_TYPE.NONE]: '未設定',
    [WATERMARK_TYPE.INDIVIDUAL_INPUT]: '個別入力',
    [WATERMARK_TYPE.CORPORATE_LOGO]: '企業ロゴ'
  } as const;

  static get(key: keyof typeof WatermarkLabel.labels): string {
    return this.labels[key];
  }
}

/**
 * マイページ設定モーダル
 * ユーザーのプロファイル情報とサービス設定を管理
 */
const MyPageModal = (props: any) => {
  const { currentUser, serviceSettings, onClose, initialTab = 'profile', initialExpandWatermark = false } = props;
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<'profile' | 'service'>(initialTab);

  // アコーディオンの展開状態
  const [isWatermarkExpanded, setIsWatermarkExpanded] = useState<boolean>(initialExpandWatermark);

  // サービス設定の状態
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [hasSettingsChanged, setHasSettingsChanged] = useState<boolean>(false);
  const [showConfirmClose, setShowConfirmClose] = useState<boolean>(false);

  // プロファイルデータの動的生成：DB情報を反映
  const generateProfileData = (): ProfileData[] => {
    const { userName, creditConsumed = 0, creditLimit = 0, isLimitInfinity = false } = currentUser || {};
    const account = currentUser?.account;

    const planName = account?.plan_type?.name || '未設定';
    const remainingCredits = Math.max(0, creditLimit - creditConsumed);

    return [
      { label: 'アカウントID', value: userName || '未設定' },
      { label: 'ご利用プラン', value: planName },
      {
        label: '実行回数',
        value: (
          <span>
            {isLimitInfinity
              ? <>実行回数 <b>{creditConsumed}回/上限なし(アカウントのクレジット上限)</b></>
              : <>残り <b>{remainingCredits}回</b> / {creditLimit}回</>
            }
          </span>
        )
      },
    ];
  };

  const profileData = generateProfileData();

  // ウォーターマークラジオボタンの選択肢定義
  const watermarkRadioButtons = [
    { type: WATERMARK_TYPE.NONE, label: WatermarkLabel.get(WATERMARK_TYPE.NONE) },
    { type: WATERMARK_TYPE.INDIVIDUAL_INPUT, label: WatermarkLabel.get(WATERMARK_TYPE.INDIVIDUAL_INPUT) },
    { type: WATERMARK_TYPE.CORPORATE_LOGO, label: WatermarkLabel.get(WATERMARK_TYPE.CORPORATE_LOGO) }
  ] as const;

  // サービス設定の保存処理
  const handleSaveSettings = async (): Promise<void> => {
    if (!hasSettingsChanged) return;

    // バリデーションチェック
    if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
      if (!serviceSettings.corporate_logo_file_data) {
        dispatch(serviceSettingsActions.setError('企業ロゴ画像を登録してください'));
        return;
      }
    } else if (serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT) {
      if (!serviceSettings.individual_input_text?.trim()) {
        dispatch(serviceSettingsActions.setError('個別入力のテキストを入力してください'));
        return;
      }
    }

    const result = await dispatch(serviceSettingsActions.saveServiceSettings());
    setHasSettingsChanged(false);
    
    // 設定保存後のコールバックを呼ぶ
    if (props.onSettingsSaved) {
      props.onSettingsSaved();
    }
  };

  // コンポーネントマウント時にサービス設定を取得
  useEffect(() => {
    dispatch(serviceSettingsActions.fetchServiceSettings());
  }, [dispatch]);


  // モーダル表示時に背景のスクロールを無効化
  useDisableBodyScroll();

  // メッセージの自動消去
  useEffect(() => {
    if (serviceSettings.success_message) {
      const timer = setTimeout(() => dispatch(serviceSettingsActions.clearMessages()), 3000);
      return () => clearTimeout(timer);
    }
  }, [serviceSettings.success_message, dispatch]);

  useEffect(() => {
    if (serviceSettings.error) {
      const timer = setTimeout(() => dispatch(serviceSettingsActions.clearMessages()), 3000);
      return () => clearTimeout(timer);
    }
  }, [serviceSettings.error, dispatch]);

  // ロゴ画像ファイル選択処理
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target?.result as string;
        dispatch(serviceSettingsActions.setCorporateLogoFile({
          name: file.name,
          data: base64Data
        }));
        // 企業ロゴを設定した際、自動的に表示をONにする
        dispatch(serviceSettingsActions.setCorporateLogoIsVisible(true));
        setHasSettingsChanged(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // ウォーターマークタイプの変更
  const handleTypeChange = (type: WatermarkType) => {
    dispatch(serviceSettingsActions.setWatermarkType(type));
    
    // 企業ロゴに変更した場合、自動的に表示をONにする
    if (type === WATERMARK_TYPE.CORPORATE_LOGO && serviceSettings.corporate_logo_file_data) {
      dispatch(serviceSettingsActions.setCorporateLogoIsVisible(true));
    }
    
    setHasSettingsChanged(true);
  };

  // 透明度とスケールの値を安全に取得するヘルパー関数
  const getTransparencyValue = () => {
    if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
      return serviceSettings.corporate_logo_transparency ?? 100;
    }
    return serviceSettings.individual_input_transparency ?? 100;
  };

  const getScaleValue = () => {
    if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
      return serviceSettings.corporate_logo_scale ?? 100;
    }
    return serviceSettings.individual_input_scale ?? 100;
  };

  // モーダルを閉じる前の確認
  const handleClose = () => {
    if (hasSettingsChanged) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // 確認後に実際に閉じる
  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // ウォーターマークのフォントサイズを計算する関数
  const calculateWatermarkFontSize = (text: string, scale: number) => {
    const containerWidth = 346; // 実測値に基づくプレビューコンテナ幅
    const previewScale = containerWidth / 1920; // 一般的な画像サイズ(1920px)との比率
    const margin = 8; // プレビュー用マージン
    const maxTextWidth = containerWidth - (margin * 2);

    // 実際の実装と同じサイズ計算
    const actualSizeRatio = 0.02 + (scale / 100) * 0.13;
    const calculatedFontSize = containerWidth * actualSizeRatio;
    const minFontSize = 12 * previewScale; // 最小値を12pxに調整してより多くの文字が入るように
    const maxFontSize = containerWidth * 0.15;
    let fontSize = Math.max(minFontSize, Math.min(maxFontSize, calculatedFontSize));

    // テキスト幅を推定（文字数ベース）
    // 文字の種類に応じて係数を調整
    let charWidthRatio = 0.6; // デフォルト（英数字）
    
    // 日本語文字の数をカウント
    const japaneseChars = (text.match(/[\u3000-\u9fff\uff00-\uffef]/g) || []).length;
    const japaneseRatio = japaneseChars / text.length;
    
    if (japaneseRatio > 0.8) {
      // ほぼ日本語
      charWidthRatio = 0.9;
    } else if (japaneseRatio > 0.2) {
      // 日本語と英語の混在
      charWidthRatio = 0.7;
    } else if (/[mwMW]/.test(text)) {
      // 幅広英字が含まれる
      charWidthRatio = 0.85;
    }
    
    const estimatedTextWidth = text.length * fontSize * charWidthRatio;

    // はみ出し防止処理（実際の実装と同じ）
    if (estimatedTextWidth > maxTextWidth) {
      const scaleFactor = maxTextWidth / estimatedTextWidth;
      fontSize = Math.max(minFontSize, fontSize * scaleFactor);
    }

    return fontSize;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.18)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 18,
        width: '70vw',
        height: '80vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        position: 'relative',
        padding: 0,
        overflow: 'hidden'
      }}>
        {/* エラー・成功メッセージ */}
        {(serviceSettings.error || serviceSettings.success_message) && (
          <div style={{
            position: 'absolute',
            top: 80,  // 閉じるボタンの下部に表示
            right: 20,
            background: serviceSettings.error ? '#f44336' : '#4caf50',
            color: '#fff',
            padding: '8px 16px',
            borderRadius: 4,
            zIndex: 1001,
            fontSize: 14
          }}>
            {serviceSettings.error || serviceSettings.success_message}
          </div>
        )}

        {/* サイドメニュー */}
        <aside style={{
          width: '25%',
          background: '#f7f7f7',
          padding: '32px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <h2 style={{ fontWeight: 600, fontSize: 20, color: '#222', textAlign: 'center' }}>アカウント設定</h2>
          {[
            { key: 'profile' as const, label: 'プロファイル', icon: <UserCircleIcon /> },
            { key: 'service' as const, label: 'サービス設定', icon: <UserSettingsIcon /> },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setActiveTab(item.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 16,
                fontWeight: 500,
                background: activeTab === item.key ? '#fff' : 'transparent',
                color: activeTab === item.key ? '#222' : '#aaa',
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                padding: '10px 12px',
                borderRadius: 8,
                margin: '0',
                boxShadow: activeTab === item.key ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
                {item.icon}
              </span>
              {item.label}
            </button>
          ))}
        </aside>

        {/* メインコンテンツエリア */}
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* ヘッダー部分 */}
          <div style={{ padding: '32px 24px 8px 24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#222' }}>
                {activeTab === 'profile' ? 'プロファイル' : 'サービス設定'}
              </h3>
              <button onClick={handleClose} style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666'
              }}>
                閉じる
              </button>
            </div>
            <div style={{ height: 1, background: '#bbb', marginTop: 12 }}></div>
          </div>

          {/* コンテンツ部分（スクロール可能） */}
          <div style={{ flex: 1, padding: '0 24px 32px 24px', overflowY: 'auto' }}>
            {/* プロファイルタブ */}
            {activeTab === 'profile' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                <tbody>
                  {profileData.map((row, index) => (
                    <tr key={row.label} style={{
                      borderBottom: index < profileData.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      <td style={{ color: '#666', padding: '20px 0', width: 140 }}>{row.label}</td>
                      <td style={{ color: '#222', padding: '20px 0', fontFamily: 'monospace' }}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* サービス設定タブ */}
            {activeTab === 'service' && (
              <div>
                {serviceSettings.loading && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    設定を読み込んでいます...
                  </div>
                )}

                {!serviceSettings.loading && (
                   <div style={{ padding: '0' }}>
                     {/* デフォルト設定セクション */}
                     <div style={{ marginBottom: 32 }}>
                       <div style={{ fontWeight: 'bold', fontSize: 16, margin: '8px 0', color: '#222' }}>デフォルト設定</div>
                       <div style={{
                         background: '#f5f5f5',
                         borderRadius: 8,
                         padding: 16,
                         marginBottom: 8
                       }}>
                         <div style={{ marginBottom: 16 }}>
                           <label style={{
                             display: 'block',
                             marginBottom: 8,
                             color: '#444',
                             fontSize: 14,
                             fontWeight: 500
                           }}>
                             部屋タイプ
                           </label>
                           <select
                             value={serviceSettings.default_room_type || ''}
                             onChange={(e) => {
                               dispatch(serviceSettingsActions.setDefaultRoomType(e.target.value || null));
                               // ワンルームが選択された場合、生活スタイルをクリア
                               if (e.target.value === 'oneRoom') {
                                 dispatch(serviceSettingsActions.setDefaultLifeStyle(null));
                               }
                               setHasSettingsChanged(true);
                             }}
                             style={{
                               width: '100%',
                               padding: '8px 12px',
                               borderRadius: 4,
                               border: '1px solid #ccc',
                               fontSize: 14,
                               background: 'white'
                             }}
                           >
                             <option value="">未設定</option>
                             {Object.entries(RoomType).map(([label, value]) => (
                               <option key={value} value={value}>
                                 {label}
                               </option>
                             ))}
                           </select>
                         </div>

                         <div>
                           <label style={{
                             display: 'block',
                             marginBottom: 8,
                             color: serviceSettings.default_room_type === 'oneRoom' ? '#999' : '#444',
                             fontSize: 14,
                             fontWeight: 500
                           }}>
                             生活スタイル{serviceSettings.default_room_type === 'oneRoom' && ' (ワンルームでは選択不可)'}
                           </label>
                           <select
                             value={serviceSettings.default_room_type === 'oneRoom' ? '' : serviceSettings.default_life_style || ''}
                             onChange={(e) => {
                               dispatch(serviceSettingsActions.setDefaultLifeStyle(e.target.value || null));
                               setHasSettingsChanged(true);
                             }}
                             disabled={serviceSettings.default_room_type === 'oneRoom'}
                             style={{
                               width: '100%',
                               padding: '8px 12px',
                               borderRadius: 4,
                               border: '1px solid #ccc',
                               fontSize: 14,
                               background: serviceSettings.default_room_type === 'oneRoom' ? '#f5f5f5' : 'white',
                               cursor: serviceSettings.default_room_type === 'oneRoom' ? 'not-allowed' : 'pointer',
                               opacity: serviceSettings.default_room_type === 'oneRoom' ? 0.6 : 1
                             }}
                           >
                             <option value="">未設定</option>
                             {Object.entries(LifestyleType).map(([label, value]) => (
                               <option key={value} value={value}>
                                 {label}
                               </option>
                             ))}
                           </select>
                         </div>
                       </div>
                       <div style={{
                         fontSize: 12,
                         color: '#666',
                         lineHeight: 1.5
                       }}>
                         ※AIステージング画面で新規作成時のデフォルト値として使用されます
                       </div>
                     </div>

                     {/* ウォーターマークセクション */}
                     <div style={{ fontWeight: 'bold', fontSize: 16, margin: '8px 0 ', color: '#222' }}>ウォーターマーク</div>
                     {/* タブ切替と透明度制御 */}
                     <div style={{
                       display: 'flex',
                       flexDirection: 'column',
                       gap: 12,
                       marginBottom: 16
                     }}>
                       {/* ラジオボタン */}
                       <div style={{
                         display: 'flex',
                         gap: 16,
                         padding: '8px 0'
                       }}>
                         {watermarkRadioButtons.map(watermarkType => (
                           <label
                             key={watermarkType.type}
                             style={{
                               display: 'flex',
                               alignItems: 'center',
                               gap: 8,
                               cursor: 'pointer',
                               userSelect: 'none',
                               color: '#444'
                             }}
                           >
                             <input
                               type="radio"
                               name="watermark-type"
                               checked={serviceSettings.watermark_type === watermarkType.type}
                               onChange={() => handleTypeChange(watermarkType.type)}
                               style={{
                                 cursor: 'pointer',
                                 width: 16,
                                 height: 16
                               }}
                             />
                             <span style={{ fontSize: 15 }}>{watermarkType.label}</span>
                           </label>
                         ))}
                       </div>
                       {serviceSettings.watermark_type !== WATERMARK_TYPE.NONE && (
                       <div style={{ display: 'flex', gap: 16 }}>
                         {/* 透明度 */}
                         <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '50%' }}>
                           <label style={{ color: '#444', fontSize: 15, whiteSpace: 'nowrap' }}>透明度：</label>
                           <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                             <input
                               type="range"
                               min={0}
                               max={100}
                               value={getTransparencyValue()}
                               onChange={e => {
                                 const newTransparency = Math.min(Math.max(0, Number(e.target.value)), 100);
                                 if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                   dispatch(serviceSettingsActions.setCorporateLogoTransparency(newTransparency));
                                 } else {
                                   dispatch(serviceSettingsActions.setIndividualInputTransparency(newTransparency));
                                 }
                                 setHasSettingsChanged(true);
                               }}
                               style={{ flex: 1 }}
                             />
                             <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 70 }}>
                               <input
                                 type="number"
                                 min={0}
                                 max={100}
                                 value={getTransparencyValue()}
                                 onBlur={e => {
                                   const newTransparency = Math.min(Math.max(0, Number(e.target.value)), 100);
                                   if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                     dispatch(serviceSettingsActions.setCorporateLogoOpacity(newTransparency));
                                   } else {
                                     dispatch(serviceSettingsActions.setIndividualInputOpacity(newTransparency));
                                   }
                                 }}
                                 onChange={e => {
                                   const newTransparency = Math.min(Math.max(0, Number(e.target.value)), 100);
                                   if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                     dispatch(serviceSettingsActions.setCorporateLogoOpacity(newTransparency));
                                   } else {
                                     dispatch(serviceSettingsActions.setIndividualInputOpacity(newTransparency));
                                   }
                                 }}
                                 style={{
                                   width: 50,
                                   fontSize: 15,
                                   padding: '2px 4px',
                                   borderRadius: 4,
                                   border: '1px solid #bbb',
                                   textAlign: 'right'
                                 }}
                               />
                               <span style={{ fontSize: 15, color: '#666' }}>%</span>
                             </div>
                           </div>
                         </div>
                         {/* スケール */}
                         <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '50%' }}>
                           <label style={{ color: '#444', fontSize: 15, whiteSpace: 'nowrap' }}>サイズ：</label>
                           <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                             <input
                               type="range"
                               min={0}
                               max={100}
                               value={getScaleValue()}
                               onChange={e => {
                                 const newScale = Math.min(Math.max(0, Number(e.target.value)), 100);
                                 if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                   dispatch(serviceSettingsActions.setCorporateLogoScale(newScale));
                                 } else {
                                   dispatch(serviceSettingsActions.setIndividualInputScale(newScale));
                                 }
                                 setHasSettingsChanged(true);
                               }}
                               style={{ flex: 1 }}
                             />
                             <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 70 }}>
                               <input
                                 type="number"
                                 min={0}
                                 max={100}
                                 value={getScaleValue()}
                                 onBlur={e => {
                                   const newScale = Math.min(Math.max(0, Number(e.target.value)), 100);
                                   if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                     dispatch(serviceSettingsActions.setCorporateLogoScale(newScale));
                                   } else {
                                     dispatch(serviceSettingsActions.setIndividualInputScale(newScale));
                                   }
                                 }}
                                 onChange={e => {
                                   const newScale = Math.min(Math.max(0, Number(e.target.value)), 100);
                                   if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO) {
                                     dispatch(serviceSettingsActions.setCorporateLogoScale(newScale));
                                   } else {
                                     dispatch(serviceSettingsActions.setIndividualInputScale(newScale));
                                   }
                                 }}
                                 style={{
                                   width: 50,
                                   fontSize: 15,
                                   padding: '2px 4px',
                                   borderRadius: 4,
                                   border: '1px solid #bbb',
                                   textAlign: 'right'
                                 }}
                               />
                               <span style={{ fontSize: 15, color: '#666' }}>%</span>
                             </div>
                           </div>
                         </div>
                       </div>
                       )}
                     </div>
                     {/* 個別入力 設定領域 */}
                     {serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT && (
                       <div>
                         {/* 入力UI */}
                         <div style={{
                           display: 'flex',
                           alignItems: 'flex-start',
                           gap: 16,
                           marginBottom: 8
                         }}>
                           <textarea
                             value={serviceSettings.individual_input_text || ''}
                             onChange={e => {
                               const newValue = e.target.value.slice(0, 30); // 30文字に制限
                               dispatch(serviceSettingsActions.setIndividualInputText(newValue));
                               setHasSettingsChanged(true);
                             }}
                             style={{
                               fontSize: 16,
                               padding: '8px 12px',
                               borderRadius: 6,
                               border: '1px solid #bbb',
                               flex: 1,
                               height: '38px',
                               minHeight: '38px',
                               resize: 'vertical',
                               lineHeight: '20px'
                             }}
                             maxLength={30}
                             placeholder="テキストを入力（最大30文字）"
                           />
                           <div style={{
                             display: 'flex',
                             alignItems: 'center',
                             gap: 8
                           }}>
                             <label style={{ color: '#444', fontSize: 15, whiteSpace: 'nowrap' }}>文字色：</label>
                             <select
                               value={serviceSettings.individual_input_color}
                               onChange={e => {
                                 dispatch(serviceSettingsActions.setIndividualInputColor(e.target.value));
                                 setHasSettingsChanged(true);
                               }}
                               style={{
                                 fontSize: 15,
                                 padding: '4px 12px',
                                 borderRadius: 4,
                                 border: '1px solid #bbb',
                                 background: '#fafafa'
                               }}
                             >
                               <option value="#ffffff">白色</option>
                               <option value="#000000">黒色</option>
                             </select>
                           </div>
                           <button
                             type="button"
                             onClick={() => {
                               dispatch(serviceSettingsActions.setIndividualInputIsVisible(
                                 !serviceSettings.individual_input_is_visible
                               ));
                               setHasSettingsChanged(true);
                             }}
                             style={{
                               background: serviceSettings.individual_input_is_visible ? '#f5f5f5' : '#999',
                               color: serviceSettings.individual_input_is_visible ? '#333' : '#fff',
                               border: serviceSettings.individual_input_is_visible ? '1px solid #ddd' : 'none',
                               borderRadius: 4,
                               padding: '4px 12px',
                               fontSize: 14,
                               fontWeight: 500,
                               outline: 'none',
                               cursor: 'pointer'
                             }}
                           >
                             {serviceSettings.individual_input_is_visible ? '表示中' : '非表示'}
                           </button>
                         </div>
                         {/* ヘッダー行 */}
                         <div style={{
                           display: 'flex',
                           gap: 16,
                           marginBottom: 8
                         }}>
                           <div style={{
                             flex: 1,
                             textAlign: 'center',
                             color: '#444',
                             fontSize: 15,
                             fontWeight: 500
                           }}>
                             個別入力
                           </div>
                           <div style={{
                             flex: 1,
                             textAlign: 'center',
                             color: '#444',
                             fontSize: 15,
                             fontWeight: 500
                           }}>
                             プレビュー
                           </div>
                         </div>
                         {/* コンテンツ行 */}
                         <div style={{
                           display: 'flex',
                           gap: 16
                         }}>
                           {/* 左側: テキストプレビュー */}
                           <div style={{ flex: 1 }}>
                             <div style={{
                               border: '2px solid #bbb',
                               borderRadius: 12,
                               background: serviceSettings.individual_input_color === '#ffffff' ? '#333333' : '#ffffff',
                               textAlign: 'center',
                               width: '100%',
                               height: '100%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               overflow: 'hidden'
                             }}>
                               {serviceSettings.individual_input_text && serviceSettings.individual_input_is_visible ? (
                                 <div style={{
                                   width: 'calc(100% - 16px)', // 左右8pxずつマージン
                                   maxWidth: 'calc(100% - 16px)',
                                   overflow: 'hidden',
                                   color: serviceSettings.individual_input_color,
                                   fontSize: `${calculateWatermarkFontSize(serviceSettings.individual_input_text, serviceSettings.individual_input_scale)}px`,
                                   fontWeight: 'bold',
                                  fontFamily: 'Arial, sans-serif',
                                   padding: '0',
                                   opacity: (100 - serviceSettings.individual_input_transparency) / 100,
                                  textAlign: 'center',
                                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                                 whiteSpace: 'nowrap'
                                 }}>
                                   {serviceSettings.individual_input_text}
                                 </div>
                               ) : (
                                 <div style={{
                                   color: '#888',
                                   fontSize: 20
                                 }}>
                                   テキストが表示されます
                                 </div>
                               )}
                             </div>
                           </div>
                           {/* 右側: プレビュー */}
                           <div style={{ flex: 1 }}>
                             <div style={{
                               position: 'relative',
                               width: '100%',
                               borderRadius: 8,
                               overflow: 'hidden',
                               border: '1px solid #ddd',
                               boxSizing: 'border-box',
                               background: '#fafbfc',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center'
                             }}>
                               <img
                                 src={watermarkPreviewImage}
                                 alt="individual watermark preview"
                                 style={{
                                   maxWidth: '100%',
                                   maxHeight: '100%',
                                   width: 'auto',
                                   height: 'auto',
                                   objectFit: 'contain',
                                   display: 'block'
                                 }}
                               />
                               {serviceSettings.individual_input_text && serviceSettings.individual_input_is_visible && (
                                 <div style={{
                                   position: 'absolute',
                                   top: '50%',
                                   left: '50%',
                                   width: 'calc(100% - 16px)', // 左右8pxずつマージン
                                   maxWidth: 'calc(100% - 16px)',
                                   padding: '0',
                                   boxSizing: 'border-box',
                                   transform: 'translate(-50%, -50%)',
                                   overflow: 'hidden', // はみ出し防止
                                   color: serviceSettings.individual_input_color,
                                   fontSize: `${calculateWatermarkFontSize(serviceSettings.individual_input_text, serviceSettings.individual_input_scale)}px`,
                                   fontWeight: 'bold',
                                  fontFamily: 'Arial, sans-serif',
                                   opacity: (100 - serviceSettings.individual_input_transparency) / 100,
                                  textAlign: 'center',
                                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                                 whiteSpace: 'nowrap'
                                 }}>
                                   {serviceSettings.individual_input_text}
                                 </div>
                               )}
                             </div>
                           </div>
                         </div>
                       </div>
                     )}
                     {/* 企業ロゴ 設定領域 */}
                     {serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO && (
                       <div>
                         {/* ヘッダー行 */}
                         <div style={{
                           display: 'grid',
                           gridTemplateColumns: '50% 50%',
                           gap: 16,
                           marginBottom: 8
                         }}>
                           <div style={{
                             textAlign: 'center',
                             color: '#444',
                             fontSize: 15,
                             fontWeight: 500
                           }}>
                             企業ロゴ画像
                           </div>
                           <div style={{
                             textAlign: 'center',
                             color: '#444',
                             fontSize: 15,
                             fontWeight: 500
                           }}>
                             プレビュー
                           </div>
                         </div>
                         {/* コンテンツ行 */}
                         <div style={{
                           display: 'grid',
                           gridTemplateColumns: '50% 50%',
                           gap: 16
                         }}>
                           {/* 左側: ロゴプレビュー */}
                           <div>
                             <div style={{
                               border: '2px dashed #bbb',
                               borderRadius: 12,
                               background: '#fafbfc',
                               textAlign: 'center',
                               width: '100%',
                               height: '100%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               overflow: 'hidden'
                             }}>
                               {serviceSettings.corporate_logo_file_data ? (
                                 <div style={{
                                   position: 'relative',
                                   width: '100%',
                                   height: '100%',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <img
                                     src={serviceSettings.corporate_logo_file_data}
                                     alt="logo preview"
                                     style={{
                                       position: 'absolute',
                                       top: '50%',
                                       left: '50%',
                                       transform: 'translate(-50%, -50%)',
                                      width: `${serviceSettings.corporate_logo_scale}%`,
                                       maxWidth: '100%',
                                       maxHeight: '100%',
                                       width: 'auto',
                                       height: 'auto',
                                       objectFit: 'contain',
                                       opacity: (100 - serviceSettings.corporate_logo_transparency) / 100
                                     }}
                                   />
                                   <button
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       dispatch(serviceSettingsActions.setCorporateLogoFile({ name: null, data: null }));
                                       setSelectedFileName('');
                                     }}
                                     style={{
                                       position: 'absolute',
                                       top: '8px',
                                       right: '8px',
                                       width: '24px',
                                       height: '24px',
                                       background: 'rgba(220, 53, 69, 0.9)',
                                       color: 'white',
                                       border: 'none',
                                       borderRadius: '50%',
                                       fontSize: '16px',
                                       fontWeight: 'bold',
                                  fontFamily: 'Arial, sans-serif',
                                       cursor: 'pointer',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       zIndex: 10,
                                       transition: 'all 0.2s ease'
                                     }}
                                     onMouseEnter={(e) => {
                                       e.currentTarget.style.background = 'rgba(220, 53, 69, 1)';
                                       e.currentTarget.style.transform = 'scale(1.1)';
                                     }}
                                     onMouseLeave={(e) => {
                                       e.currentTarget.style.background = 'rgba(220, 53, 69, 0.9)';
                                       e.currentTarget.style.transform = 'scale(1)';
                                     }}
                                     title="削除"
                                   >
                                     ×
                                   </button>
                                 </div>
                               ) : (
                                 <div style={{
                                   display: 'flex',
                                   flexDirection: 'column',
                                   alignItems: 'center',
                                   gap: 8,
                                   padding: '32px 0'
                                 }}>
                                   <SelectFileIcon className="w-12 h-12 text-gray-500" />
                                   <label style={{ display: 'inline-block', cursor: 'pointer' }}>
                                     <input type="file" accept="image/png" style={{ display: 'none' }}
                                       onChange={handleLogoChange} />
                                     <span style={{
                                       background: '#1976d2',
                                       color: '#fff',
                                       borderRadius: 6,
                                       padding: '6px 18px',
                                       cursor: 'pointer',
                                       fontWeight: 500,
                                       fontSize: 15
                                     }}>
                                       画像を選択
                                     </span>
                                   </label>
                                 </div>
                               )}
                             </div>
                             {!serviceSettings.corporate_logo_file_name && (
                               <div style={{
                                 color: '#444',
                                 fontSize: 14,
                                 textAlign: 'center',
                                 margin: '8px 0 8px 8px'
                               }}>
                                 透過されている画像を入れてください。<br />
                                 推奨サイズは 640 x 360 です。
                               </div>
                             )}
                           </div>
                           {/* 右側: ウォーターマークプレビュー */}
                           <div>
                             <div style={{
                               position: 'relative',
                               width: '100%',
                               borderRadius: 8,
                               overflow: 'hidden',
                               border: '1px solid #ddd',
                               boxSizing: 'border-box',
                               background: '#fafbfc',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center'
                             }}>
                               <img
                                 src={watermarkPreviewImage}
                                 alt="corporate watermark preview"
                                 style={{
                                   maxWidth: '100%',
                                   maxHeight: '100%',
                                   width: 'auto',
                                   height: 'auto',
                                   objectFit: 'contain',
                                   display: 'block'
                                 }}
                               />
                               {serviceSettings.corporate_logo_file_data && (
                                 <img
                                   src={serviceSettings.corporate_logo_file_data}
                                   alt="watermark preview"
                                   style={{
                                     position: 'absolute',
                                     top: '50%',
                                     left: '50%',
                                     transform: `translate(-50%, -50%) scale(${serviceSettings.corporate_logo_scale / 100})`,
                                     maxWidth: '100%',
                                     maxHeight: '100%',
                                     width: 'auto',
                                     height: 'auto',
                                     objectFit: 'contain',
                                     opacity: (100 - serviceSettings.corporate_logo_transparency) / 100
                                   }}
                                 />
                               )}
                             </div>
                           </div>
                         </div>
                         {serviceSettings.corporate_logo_file_name && (
                           <div style={{
                             color: '#444',
                             fontSize: 14,
                             textAlign: 'left',
                             margin: '8px 0 8px 8px'
                           }}>
                             ファイル名： {serviceSettings.corporate_logo_file_name}
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                )}
              </div>
            )}
          </div>

          {/* フッター部分（サービス設定タブのみ） */}
          {activeTab === 'service' && (
            <div style={{
              borderTop: '1px solid #eee',
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              background: '#fafafa'
            }}>
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={serviceSettings.loading || !hasSettingsChanged}
                style={{
                  background: hasSettingsChanged ? '#ff9800' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '12px 24px',
                  fontWeight: 500,
                  fontSize: 16,
                  cursor: hasSettingsChanged ? 'pointer' : 'not-allowed',
                  boxShadow: hasSettingsChanged ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                {serviceSettings.loading ? '保存中...' : '保存'}
              </button>
            </div>
          )}
        </section>
      </div>

      {/* 確認ダイアログ */}
      {showConfirmClose && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 'bold' }}>
              サービス設定の編集内容を破棄しますか？
            </h3>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowConfirmClose(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleConfirmClose}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#ff4444',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                破棄
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(mapStateToProps)(MyPageModal);
